'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { EventForm, type EventFormRef } from '@/components/partials/events'
import { ContentError, FormSkeleton } from '@/components/partials'
import { useEvent, useUpdateEvent } from '@/hooks'
import { type CreateEventParams } from '@/types/event'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const eventFormRef = useRef<EventFormRef>(null)

  const { data: event, isLoading, error } = useEvent(id)
  const { mutate: updateEvent, isPending } = useUpdateEvent()

  const handleSubmit = (data: CreateEventParams) => {
    updateEvent(
      { id, params: data },
      {
        onSuccess: async updatedEvent => {
          setIsRedirecting(true)
          queryClient.setQueryData(['event', id], updatedEvent)
          await queryClient.invalidateQueries({ queryKey: ['events'] })
          toast.success(t('updated_successfully'))
          router.push(`/events/${updatedEvent.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (eventFormRef.current) {
        eventFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/events/${id}`)
  }

  if (isLoading || !event) {
    return <FormSkeleton fieldRows={2} showPictures />
  }

  if (error || !event) {
    return <ContentError error={error} listUrl="/events" />
  }

  return (
    <EventForm
      ref={eventFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={event}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
